import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { ChromePicker } from 'react-color';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    IconButton,
    InputAdornment,
    Popover
} from "@material-ui/core";

import { Colorize } from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    colorAdornment: {
        width: 20,
        height: 20,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const TagSchema = Yup.object().shape({
    name: Yup.string()
        .min(3, "Mensagem muito curta")
        .required("Obrigatório"),
    color: Yup.string().required("Obrigatório"),
});

const TagModal = ({ open, onClose, tagId, reload }) => {
    const classes = useStyles();
    const [colorPickerAnchor, setColorPickerAnchor] = useState(null);

    const initialState = {
        name: "",
        color: "#000000",
    };

    const [tag, setTag] = useState(initialState);

    useEffect(() => {
        try {
            const fetchTag = async () => {
                if (!tagId) return;

                const { data } = await api.get(`/tags/${tagId}`);
                setTag(prevState => ({ ...prevState, ...data }));
            };
            fetchTag();
        } catch (err) {
            toastError(err);
        }
    }, [tagId, open]);

    const handleClose = () => {
        setTag(initialState);
        setColorPickerAnchor(null);
        onClose();
    };

    const handleSaveTag = async values => {
        const tagData = { ...values };
        try {
            if (tagId) {
                await api.put(`/tags/${tagId}`, tagData);
            } else {
                await api.post("/tags", tagData);
            }
            toast.success(i18n.t("tags.toasts.success"));
            if (reload) reload();
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {tagId
                        ? `${i18n.t("tags.title")} - Editar`
                        : `${i18n.t("tags.title")} - Adicionar`}
                </DialogTitle>
                <Formik
                    initialValues={tag}
                    enableReinitialize={true}
                    validationSchema={TagSchema}
                    onSubmit={(values, { setSubmitting }) => {
                        setTimeout(() => {
                            handleSaveTag(values);
                            setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <div className={classes.root}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("tags.form.name")}
                                        name="name"
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textField}
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("tags.form.color")}
                                        name="color"
                                        id="color"
                                        error={touched.color && Boolean(errors.color)}
                                        helperText={touched.color && errors.color}
                                        variant="outlined"
                                        margin="dense"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <div style={{ backgroundColor: values.color, width: 20, height: 20, borderRadius: 4 }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <IconButton
                                                    size="small"
                                                    color="default"
                                                    onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                                                >
                                                    <Colorize />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                    <Popover
                                        open={Boolean(colorPickerAnchor)}
                                        anchorEl={colorPickerAnchor}
                                        onClose={() => setColorPickerAnchor(null)}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'left',
                                        }}
                                    >
                                        <ChromePicker
                                            color={values.color}
                                            disableAlpha={true}
                                            onChangeComplete={(color) => {
                                                setFieldValue("color", color.hex);
                                                // setColorPickerAnchor(null); // Removed to allow interaction
                                            }}
                                        />
                                    </Popover>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {tagId
                                        ? i18n.t("global.buttons.ok")
                                        : i18n.t("global.buttons.add")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default TagModal;
